/**
 * <ActivityRow> — une activité, sur une ligne (molecule).
 *
 * La même ligne sert les trois surfaces de l'onglet Activités : « À faire », « Faites » et les
 * activités archivées. Elle reprend ce que montre le legacy (`RealizationsListItemContent.js`) :
 * le picto de la matière, le titre du support, le chapitre travaillé — le nom de la matière
 * quand il n'y en a pas — puis, à droite, le chronomètre d'une activité à faire ou la date et le
 * statut d'une activité terminée.
 *
 * **Elle ne dessine pas de carte** : une liste de lignes fait UNE carte-liste. Le retrait vient
 * de `<CardListRow>`, comme `<TodoRow>` et `<SessionRow>`.
 *
 * La table statut ↔ libellé ↔ chip est interne, comme `<LiveStatusBadges>` : un appelant choisit
 * une activité, jamais la couleur de son statut.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { Checkbox } from "../../atoms/Checkbox"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../CardList"
import { formatDayLabel } from "../../../ports/primary/ui/formatters/dateFormatter"
import { activityDurationLabel } from "../../../ports/primary/ui/formatters/activityFormatter"
import { type UIActivity, type UIActivityStatus } from "../../../ports/primary/ui/types"
import { type UIActivityDurationTone } from "../../../ports/primary/ui/types/activity"

/**
 * Statut → chip. Les libellés sont ceux du legacy (`DoneChip.js`), au mot près ; les trois
 * statuts « en route » n'y portent aucun glyphe, et on n'en invente pas.
 *
 * Les chips sont **teintées** : le vert et le rouge pleins du legacy passent sous le contraste AA
 * du livre en petit texte (`DECISIONS.md`, « Deux encres ajoutées pour tenir l'AA »).
 */
const STATUS_BADGE: Record<UIActivityStatus, { label: string; variant: BadgeVariant; icon?: IconName }> = {
    todo: { label: "À faire", variant: "neutral" },
    started: { label: "En pause", variant: "success" },
    ongoing: { label: "En cours", variant: "success" },
    not_reviewed: { label: "Fait", variant: "success", icon: "check" },
    pending_review: { label: "En attente", variant: "warning", icon: "hourglass-half" },
    rejected: { label: "Rejeté", variant: "neutral", icon: "times" },
    validated: { label: "Validé", variant: "success", icon: "check-double" },
}

/**
 * Le chrono d'une activité à faire, par rapport au temps prévu : vert en deçà, orange au-delà, comme
 * le legacy (`TodoActivityBadges.js`, `calculateTimerColor`). Sans ton, la chip reste neutre.
 */
const DURATION_VARIANT: Record<UIActivityDurationTone, BadgeVariant> = { on_time: "success", over: "warning" }

export type ActivityRowProps = {
    activity: UIActivity
    /** La sélection multiple est ouverte : la ligne montre sa case à cocher. */
    selectable?: boolean
    selected?: boolean
    onPress?: (activity: UIActivity) => void
    /** Appui long — l'entrée dans la sélection multiple. */
    onLongPress?: (activity: UIActivity) => void
    /**
     * La ligne est en cours de déplacement : elle montre sa poignée et ne presse plus. Un état
     * d'affichage — c'est `<CardListDropSlot>` qui la soulève. Défaut `false`.
     */
    lifted?: boolean
    /**
     * Ce qui se pose à droite à la place du chrono (ou de la date et du statut) — `null` n'y pose
     * rien. La prescription du masteur ne montre pas la durée (le legacy y passait `hideDuration`)
     * et y pose sa marque « Quizz ». ABSENT (défaut) : le chrono ou la date, comme avant.
     */
    trailing?: React.ReactNode
    testID?: string
}

export function ActivityRow(props: ActivityRowProps) {
    const {
        activity,
        selectable = false,
        selected = false,
        onPress,
        onLongPress,
        lifted = false,
        trailing,
        testID,
    } = props
    const { title, subject, chapterName, status, finishedAt } = activity

    const badge = STATUS_BADGE[status]
    const press = onPress != null ? () => onPress(activity) : undefined

    return (
        <Pressable
            testID={testID}
            onPress={press}
            onLongPress={onLongPress != null ? () => onLongPress(activity) : undefined}
            delayLongPress={300}
            disabled={lifted || (onPress == null && onLongPress == null)}
            accessibilityRole={selectable ? "checkbox" : "button"}
            accessibilityState={selectable ? { checked: selected } : undefined}
        >
            <CardListRow>
                <Row gap="3" align="center">
                    {selectable && <Checkbox checked={selected} onChange={() => onPress?.(activity)} />}
                    {lifted && <Icon name="grip-vertical" size={20} color="text-muted" />}

                    <SubjectBadge subject={subject} />

                    <Stack gap="1" style={styles.body}>
                        <Text weight="bold" size="text-md">
                            {title}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            {chapterName ?? subject.name}
                        </Text>
                    </Stack>

                    {trailing !== undefined ? (
                        trailing
                    ) : finishedAt != null ? (
                        <Stack gap="1" align="end" style={styles.trailing}>
                            <Text size="text-xs" color="text-muted">
                                {formatDayLabel(finishedAt)}
                            </Text>
                            <Badge text={badge.label} variant={badge.variant} iconName={badge.icon} tinted />
                        </Stack>
                    ) : (
                        <Badge
                            text={activityDurationLabel(activity)}
                            variant={
                                activity.durationTone != null ? DURATION_VARIANT[activity.durationTone] : "neutral"
                            }
                            iconName="stopwatch"
                            tinted
                            testID={testID != null ? `${testID}-duration` : undefined}
                        />
                    )}
                </Row>
            </CardListRow>
        </Pressable>
    )
}

const styles = StyleSheet.create(() => ({
    body: {
        flex: 1,
        flexShrink: 1,
    },
    trailing: {
        flexShrink: 0,
    },
}))
